import { decodeGooglePolyline } from './browserMapAdapter';
import { isValidMapPosition } from './navigationUrl';

const EARTH_RADIUS_M = 6371000;
// A high-accuracy watch legitimately goes 30-60 s between fixes under tree
// cover, between buildings, or in stopped traffic. At 45 s the panel declared
// the position stale while nothing was wrong, repeatedly, mid-journey.
const STALE_AFTER_MS = 90000;
// Deliberately NOT loosened. Guidance drawn from a fix this coarse would put a
// rider confidently on the wrong road, which is worse than declining to guide.
const POOR_ACCURACY_M = 100;
const NEAR_DESTINATION_M = 80;
const OFF_ROUTE_M = 150;

function radians(value) {
  return Number(value) * Math.PI / 180;
}

export function distanceMeters(from, to) {
  if (!isValidMapPosition(from) || !isValidMapPosition(to)) return null;
  const latitudeDelta = radians(Number(to.latitude ?? to.lat) - Number(from.latitude ?? from.lat));
  const longitudeDelta = radians(Number(to.longitude ?? to.lng) - Number(from.longitude ?? from.lng));
  const fromLatitude = radians(Number(from.latitude ?? from.lat));
  const toLatitude = radians(Number(to.latitude ?? to.lat));
  const haversine = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(fromLatitude) * Math.cos(toLatitude) * Math.sin(longitudeDelta / 2) ** 2;
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

function bearingDegrees(from, to) {
  const fromLatitude = radians(Number(from.latitude ?? from.lat));
  const toLatitude = radians(Number(to.latitude ?? to.lat));
  const longitudeDelta = radians(Number(to.longitude ?? to.lng) - Number(from.longitude ?? from.lng));
  const y = Math.sin(longitudeDelta) * Math.cos(toLatitude);
  const x = Math.cos(fromLatitude) * Math.sin(toLatitude)
    - Math.sin(fromLatitude) * Math.cos(toLatitude) * Math.cos(longitudeDelta);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function compassDirection(degrees) {
  const directions = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
  return directions[Math.round(degrees / 45) % directions.length];
}

export function formatGuidanceDistance(meters) {
  if (!Number.isFinite(meters)) return 'Distance unavailable';
  if (meters < 1000) return `${Math.max(10, Math.round(meters / 10) * 10)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
}

function asPosition(point) {
  return point && {
    latitude: Number(point.latitude ?? point.lat),
    longitude: Number(point.longitude ?? point.lng),
  };
}

function projectToSegment(target, start, end) {
  const meanLatitude = radians((start.latitude + end.latitude + target.latitude) / 3);
  const xScale = EARTH_RADIUS_M * Math.cos(meanLatitude);
  const yScale = EARTH_RADIUS_M;
  const segmentX = radians(end.longitude - start.longitude) * xScale;
  const segmentY = radians(end.latitude - start.latitude) * yScale;
  const targetX = radians(target.longitude - start.longitude) * xScale;
  const targetY = radians(target.latitude - start.latitude) * yScale;
  const lengthSquared = segmentX ** 2 + segmentY ** 2;
  const ratio = lengthSquared === 0 ? 0
    : Math.max(0, Math.min(1, (targetX * segmentX + targetY * segmentY) / lengthSquared));
  const position = {
    latitude: start.latitude + (end.latitude - start.latitude) * ratio,
    longitude: start.longitude + (end.longitude - start.longitude) * ratio,
  };
  return { position, ratio, distanceM: distanceMeters(target, position) };
}

function nearestProjection(path, target, finalSegmentIndex = path.length - 2) {
  let nearest = null;
  for (let index = 0; index <= finalSegmentIndex; index += 1) {
    const projection = projectToSegment(target, path[index], path[index + 1]);
    if (!nearest || projection.distanceM < nearest.distanceM) nearest = { ...projection, segmentIndex: index };
  }
  return nearest;
}

function routeProgress(encodedPolyline, currentPosition, destination) {
  const path = decodeGooglePolyline(encodedPolyline).map(asPosition).filter(isValidMapPosition);
  if (path.length < 2) return null;

  const destinationProjection = nearestProjection(path, destination);
  const riderProjection = nearestProjection(path, currentPosition, destinationProjection.segmentIndex);
  if (!destinationProjection || !riderProjection) return null;

  let remainingDistanceM = riderProjection.distanceM;
  if (riderProjection.segmentIndex === destinationProjection.segmentIndex
    && riderProjection.ratio <= destinationProjection.ratio) {
    remainingDistanceM += distanceMeters(riderProjection.position, destinationProjection.position);
  } else if (riderProjection.segmentIndex < destinationProjection.segmentIndex) {
    remainingDistanceM += distanceMeters(riderProjection.position, path[riderProjection.segmentIndex + 1]);
    for (let index = riderProjection.segmentIndex + 1; index < destinationProjection.segmentIndex; index += 1) {
      remainingDistanceM += distanceMeters(path[index], path[index + 1]);
    }
    remainingDistanceM += distanceMeters(path[destinationProjection.segmentIndex], destinationProjection.position);
  } else {
    remainingDistanceM += distanceMeters(currentPosition, destination);
  }
  remainingDistanceM += destinationProjection.distanceM;

  let aheadIndex = Math.min(riderProjection.segmentIndex + 1, path.length - 1);
  while (aheadIndex < path.length - 1 && distanceMeters(riderProjection.position, path[aheadIndex]) < 25) {
    aheadIndex += 1;
  }
  return {
    distanceToRouteM: riderProjection.distanceM,
    remainingDistanceM,
    direction: compassDirection(bearingDegrees(currentPosition, path[aheadIndex])),
  };
}

function unavailable(status, title, detail) {
  return { status, tone: 'warning', title, detail, remainingDistanceM: null };
}

export function deriveRouteGuidance({
  currentPosition,
  destination,
  encodedPolyline = null,
  mode = 'DELIVERY',
  now = Date.now(),
} = {}) {
  if (!isValidMapPosition(destination)) {
    return unavailable('NO_DESTINATION', 'Destination unavailable', 'Refresh the trip or contact dispatch before travelling.');
  }
  if (!isValidMapPosition(currentPosition)) {
    return unavailable('NO_LOCATION', 'Waiting for your location', 'Keep location access on so Apparify can guide this journey.');
  }

  const capturedAt = new Date(currentPosition.capturedAt || 0).getTime();
  if (!Number.isFinite(capturedAt) || now - capturedAt > STALE_AFTER_MS) {
    const secondsOld = Math.round((now - capturedAt) / 1000);
    return unavailable('STALE_LOCATION', 'Location needs an update',
      `Your last position is ${secondsOld} seconds old. Move to an open area and wait for a fresh reading before relying on the map.`);
  }
  const accuracyM = Number(currentPosition.accuracyM ?? currentPosition.accuracy);
  if (!Number.isFinite(accuracyM) || accuracyM > POOR_ACCURACY_M) {
    // Say why, or a rider cannot tell a poor signal from a broken feature.
    return unavailable('POOR_ACCURACY', 'Improving GPS accuracy',
      `Your location is accurate to about ${Math.round(accuracyM)} m, which is not precise enough to guide you. This usually means you are indoors or under cover - move somewhere with a clear view of the sky.`);
  }

  const directDistanceM = distanceMeters(currentPosition, destination);
  if (directDistanceM <= NEAR_DESTINATION_M) {
    return {
      status: 'NEAR_DESTINATION', tone: 'success', remainingDistanceM: directDistanceM,
      title: mode === 'PICKUP' ? 'Pickup point is nearby' : 'Customer stop is nearby',
      detail: mode === 'PICKUP'
        ? 'Verify the storefront and use the arrival action only when you are physically there.'
        : 'Verify the address and use the arrival action only when you are physically there.',
    };
  }

  if (mode === 'PICKUP') {
    const direction = compassDirection(bearingDegrees(currentPosition, destination));
    return {
      status: 'DIRECT_TO_PICKUP', tone: 'neutral', remainingDistanceM: directDistanceM,
      title: `${formatGuidanceDistance(directDistanceM)} to pickup`,
      detail: `The pickup point is ${direction}. This is proximity guidance; use the road map or Google Maps fallback for street directions.`,
    };
  }

  if (!encodedPolyline) {
    return {
      status: 'NO_ROUTE', tone: 'warning', remainingDistanceM: directDistanceM,
      title: `${formatGuidanceDistance(directDistanceM)} from the customer`,
      detail: 'The saved road line is unavailable. Use the destination marker or Google Maps fallback and contact dispatch if the stop looks wrong.',
    };
  }

  const progress = routeProgress(encodedPolyline, currentPosition, destination);
  if (!progress) {
    return unavailable('INVALID_ROUTE', 'Route guidance unavailable', 'The saved route could not be shown. Use Google Maps fallback or contact dispatch.');
  }
  if (progress.distanceToRouteM > Math.max(OFF_ROUTE_M, accuracyM + 75)) {
    return {
      status: 'OFF_ROUTE', tone: 'warning', remainingDistanceM: progress.remainingDistanceM,
      title: 'You appear off the planned route',
      detail: `${formatGuidanceDistance(progress.distanceToRouteM)} from the saved road line. Recenter, use Google Maps fallback, or contact dispatch if the route is no longer usable.`,
    };
  }
  return {
    status: 'ON_ROUTE', tone: 'success', remainingDistanceM: progress.remainingDistanceM,
    title: `${formatGuidanceDistance(progress.remainingDistanceM)} remaining`,
    detail: `Continue ${progress.direction} on the planned route. Apparify keeps the delivery workflow visible while you travel.`,
  };
}
